import {type ActionListLinkItemProps, type AnchoredOverlayProps, Button, type ButtonProps, Flash} from '@primer/react'
import type {SxProp} from '@primer/styled-react'
import {TabNav} from '@primer/styled-react/deprecated'

import {RefSelectorV1} from './RefSelectorV1'

import styles from './RefSelector.module.css'
import {clsx} from 'clsx'

export interface RefSelectorFooterProps {
  text: string
  onClick?: () => void
  href?: ActionListLinkItemProps['href']
  sx?: SxProp['sx']
}

export interface RefSelectorProps {
  /**
   * The aria-describedby attribute for the button that opens the ref selector.
   */
  ariaDescribedBy?: string
  /**
   * The aria-labelledby attribute for the button that opens the ref selector.
   */
  ariaLabelledBy?: string
  /**
   * The aria-label attribute for the button that opens the ref selector.
   */
  ariaLabel?: string
  /**
   * A className to pass to the button that opens the ref selector.
   */
  buttonClassName?: string
  /**
   * The text to display on the button that opens the ref selector. If not provided,
   * this will be determined based on the `currentCommitish` prop.
   */
  buttonText?: string
  /**
   * If false, button will prioritize text based on the `currentCommitish` prop even if buttonText is provided.
   * If true, button will prioritize buttonText if provided.
   * @default true
   */
  persistButtonText?: boolean
  /**
   * The prefix to display before the button text. If not provided,
   * button text will be used as is.
   * If `buttonText` is provided, this will be prepended to it.
   * If `buttonText` is not provided, this will be prepended to the `currentCommitish` prop.
   */
  buttonPrefix?: string
  /**
   * If true, the button will show an icon before the text.
   * @default true
   */
  showIcon?: boolean
  /**
   * A key used to represent the freshness of any given list of refs.
   * In ruby, this can be constructed with `BranchesHelper#ref_list_cache_key`
   */
  cacheKey: string
  /**
   * True if the ref selector should display an option to create a branch.
   */
  canCreate: boolean
  /**
   * True if the ref selector should display an option to create a tag.
   * @default false
   */
  canCreateTag?: boolean
  /**
   * The current ref or commit being viewed.
   */
  currentCommitish: string
  /**
   * The default branch for the current repository.
   */
  defaultBranch: string
  /**
   * A function to create an href to which we should navigate when a ref is selected.
   */
  getHref?: (ref: string) => string
  /**
   * If true, the ref selector will not show the "View all branches" option at the bottom.
   */
  hideShowAll?: boolean
  /**
   * The owner of the repo.
   */
  owner: string
  /**
   * The name of the repo (without the owner).
   */
  repo: string
  /**
   * The type of the selected ref: 'branch' or 'tag'.
   * If 'tree', the ref selector will normalize to 'branch' and the display text will be the short SHA of the currentCommitish.
   */
  selectedRefType?: RefType | 'tree'
  /**
   * The types of refs that the ref selector should display.
   * By default, ['branch', 'tag']
   */
  types?: RefType[]
  /**
   * The name of the data-hotkey (e.g: "w")
   */
  hotKey?: string
  /**
   * The ending to put on the id of the ref selector button for SSR so that it doesn't change when rendering on
   * the client.
   */
  idEnding?: string
  /**
   * Optional callback that will be invoked when a ref item is clicked (mouse) or selected (keyboard).
   */
  onSelectItem?: (ref: string, refType: RefType) => void
  /**
   * If true, when a ref item is clicked (mouse) or selected (keyboard), the ref selector is closed.
   */
  closeOnSelect?: boolean
  /**
   * A function that is called if ref creation fails.
   */
  onCreateError?: (errorMessage: string) => void
  /**
   * A function that is called before making a create branch request.
   */
  onBeforeCreate?: (refName: string) => void
  /**
   * A callback that is invoked on ref selector menu state change.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * A callback that is invoked on ref selector menu state change.
   */
  onRefTypeChanged?: (refType: RefType) => void
  /**
   * Props for rendering a managed Action List/Link Item footer
   */
  customFooterItemProps?: RefSelectorFooterProps
  /**
   * Size of the button to render
   * @default 'medium' if not provided
   * Option of 'small', 'medium', or 'large'
   */
  size?: ButtonProps['size']
  /**
   * variant of the button to render
   * @default 'secondary' if not provided
   * Option of 'primary', 'secondary', 'invisible', 'danger', 'outline', 'link'
   */
  variant?: ButtonProps['variant']
  /**
   * Optional alignment prop for the view all ref item
   */
  viewAllJustify?: 'center' | 'start'
  /**
   * Optional prop to allow branch name to be > 125px
   * This is useful when the branch picker is in a resizable area ie. page layout pane
   */
  allowResizing?: boolean
  /**
   * Set to true to use the default focus zone for the anchored overlay.
   */
  useFocusZone?: boolean
  /**
   * Optional prop to set the heading text for the action list. If not provided,
   * it will be set based on the value of the `types` prop.
   */
  actionListHeadingText?: string
  /**
   * Optional prop to set the description text below the heading in the action list.
   */
  actionListDescription?: string
  /**
   * Disable the ref selector anchor button and keyboard hotkey.
   */
  disabled?: ButtonProps['disabled']
  /**
   * Props to pass to the AnchoredOverlay component that wraps the action list.
   */
  anchoredOverlayProps?: Pick<AnchoredOverlayProps, 'height' | 'variant'>
  /**
   *  Render custom anchor element instead of the default button.
   */
  renderAnchor?: AnchoredOverlayProps['renderAnchor']
}

export type RefType = 'branch' | 'tag'
export const defaultTypes: RefType[] = ['branch', 'tag']

/**
 * An action menu that allows the user to switch between branches or tags.
 *
 * TODO: Currently this does not have support for customizing how the items,
 *       zero states, and branch creation action are rendered. The old
 *       <ref-selector> element allowed that by requiring each usage to pass
 *       in templates. The right solution here is probably something like
 *       a set of render props.
 *
 * @remarks
 * This accomplishes something very similar to the <ref-selector> web component.
 * There are a few reasons that working with that component in react is difficult:
 * - React has bugs in how it handles <template> elements, and those bugs are
 *   not likely to be fixed for perf reasons: https://github.com/facebook/react/issues/19932
 *   This can be worked around, but it is very easy to break.
 * - There is no way to attach callbacks to elements that would be rendered
 *   through a template, since those DOM nodes are all going to be copied in
 *   a way that is not managed by React.
 */
export function RefSelector(props: RefSelectorProps) {
  return <RefSelectorV1 {...props} />
}

interface RefTypeTabsProps {
  refType: RefType
  onRefTypeChanged: (refType: RefType) => void
  sx?: SxProp
  className?: string
}

export function RefTypeTabs({refType, onRefTypeChanged, sx, className}: RefTypeTabsProps) {
  return (
    <TabNav
      sx={{
        ...sx,
      }}
      aria-label="Ref type"
      className={clsx(styles.RefSelectorTabs, className)}
    >
      <TabNav.Link
        as={Button}
        id="branch-button"
        aria-controls="branches"
        selected={refType === 'branch'}
        onClick={() => onRefTypeChanged('branch')}
        className={styles.RefSelectorTabLink}
      >
        Branches
      </TabNav.Link>
      <TabNav.Link
        as={Button}
        id="tag-button"
        aria-controls="tags"
        selected={refType === 'tag'}
        onClick={() => onRefTypeChanged('tag')}
        className={styles.RefSelectorTabLink}
      >
        Tags
      </TabNav.Link>
    </TabNav>
  )
}

export function LoadingFailed({refType}: {refType: RefType}) {
  return <Flash variant="danger">Could not load {refType === 'branch' ? 'branches' : 'tags'}</Flash>
}
